import React, { useEffect, useState, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import HStatCard from "src/hcomponents/HStatCard";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import HActionButton from "../../../hcomponents/HActionButton";
import ViewToggle from "../../../components/ViewToggle";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
  Calendar,
  Building,
  Users,
  CalendarDays,
  PlusCircle,
  BellRing,
  Eye,
  MoreHorizontal,
  ArrowUp,
  ArrowDown,
  Bell,
  TrendingUp,
} from "lucide-react";
import { dashboardService } from "../../../data/host-dummy-data/dashboard/dashboardService";
import spaceManagementService from "../../../services/host-apiServices/spaceManagementService";
import apiService from "../../../services/apiService";
import billingService from "../../../services/billingService";
import logger from "../../../utils/logger";
import moment from "moment";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../shadcn/ui/select";
import { DatePicker } from "../../../shadcn/ui/date-time-picker";
import * as localService from "../../../services/localService";
import {
  getUserTimezone,
  formatTimezoneDisplay,
  convertToUTC,
  getCurrentTimezoneOffset,
} from "../../../utils/timezoneUtils";
import useNotificationWebSocket from "../../../hooks/useNotificationWebSocket";
import { getEventName } from "../../../utils/spaceNamingUtils";

const HostDashboard = () => {
  const history = useHistory();
  const notificationRef = useRef(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState("Daily");
  const [selectedRevenuePeriod, setSelectedRevenuePeriod] = useState("Daily");
  const [occupancyData, setOccupancyData] = useState([]);
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState("Last 30 Days");
  const [customStartDate, setCustomStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
  const [customEndDate, setCustomEndDate] = useState(new Date());
  const [todaysBookings, setTodaysBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [bookingsError, setBookingsError] = useState(null);
  const [statistics, setStatistics] = useState({
    totalSpaces: 0,
    occupancyRate: 0,
  });
  const [bookingStatistics, setBookingStatistics] = useState({
    totalBookings: 0,
    totalActiveBookings: 0,
    percentageOccupancyRate: 0,
    activeBookingsChange: 0,
    occupancyRateChange: 0,
    activeChangeType: "NONE",
    occupancyChangeType: "NONE",
  });
  const [bookingStatisticsError, setBookingStatisticsError] = useState(null);
  const [_statisticsError, setStatisticsError] = useState(null);
  const [occupancyChartError, setOccupancyChartError] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, _setNotificationsLoading] = useState(false);
  const [notificationsError, _setNotificationsError] = useState(null);

  // Get current user ID for notification API calls
  const currentUserId = localService.getUser()?.id;

  // Helper to get date range from time range string
  const getDateRange = timeRange => {
    const today = new Date();
    const endDate = new Date(today);
    let startDate = new Date(today);

    if (timeRange === "Custom Range") {
      return {
        startDate: customStartDate.toISOString().split("T")[0],
        endDate: customEndDate.toISOString().split("T")[0],
      };
    }

    switch (timeRange) {
      case "Last 7 Days":
        startDate.setDate(today.getDate() - 7);
        break;
      case "Last 30 Days":
        startDate.setDate(today.getDate() - 30);
        break;
      case "Last 90 Days":
        startDate.setDate(today.getDate() - 90);
        break;
      case "This Month":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case "Last Month":
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate.setDate(0); // Last day of previous month
        break;
      default:
        startDate.setDate(today.getDate() - 30);
    }

    return {
      startDate: startDate.toISOString().split("T")[0], // YYYY-MM-DD format
      endDate: endDate.toISOString().split("T")[0], // YYYY-MM-DD format
    };
  };

  // Mark a single notification as read
  const markNotificationAsRead = async notificationId => {
    if (!currentUserId) return;
    try {
      const response = await apiService.markNotificationAsRead(notificationId, currentUserId);
      if (response && (response.status === 200 || response.status === 204)) {
        setNotifications(prev =>
          prev.map(n => (n.id === notificationId ? { ...n, unread: false, readStatus: true } : n))
        );
      }
    } catch (error) {
      logger.error("Error marking notification as read:", error);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsAsRead = async () => {
    if (!currentUserId || notifications.length === 0) return;
    try {
      await apiService.markAllNotificationsAsRead(currentUserId);
      setNotifications(prev => prev.map(n => ({ ...n, unread: false, readStatus: true })));
    } catch (error) {
      logger.error("Error marking all notifications as read:", error);
    }
  };

  // Helper to parse date from various formats (Java date object, ISO string, or timestamp)
  const parseNotificationDate = useCallback(dateValue => {
    if (!dateValue) return new Date();

    // Handle Java date object format
    if (dateValue.year !== undefined && dateValue.monthValue !== undefined) {
      return new Date(
        dateValue.year,
        dateValue.monthValue - 1, // JS months are 0-indexed
        dateValue.dayOfMonth || 1,
        dateValue.hour || 0,
        dateValue.minute || 0,
        dateValue.second || 0
      );
    }

    // Handle ISO string or timestamp
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  }, []);

  // Helper to format a notification from WebSocket or REST API payload
  const formatNotification = useCallback(
    notification => ({
      id: notification.id || Date.now(),
      title: notification.title || "Notification",
      message: notification.message || notification.body || notification.content || "",
      time: notification.createdAt ? moment(parseNotificationDate(notification.createdAt)).fromNow() : "Recently",
      unread: !notification.readStatus,
      type: "announcement",
      priority: notification.priority || "LOW",
      cta: notification.cta || false,
      readStatus: notification.readStatus || false,
    }),
    [parseNotificationDate]
  );

  // WebSocket for real-time notifications
  useNotificationWebSocket(
    // Callback for individual real-time notifications
    useCallback(
      notification => {
        logger.log("🔔 Real-time notification received:", notification);
        const formattedNotification = formatNotification(notification);
        setNotifications(prev => {
          // Check if notification already exists (by id)
          if (prev.some(n => n.id === notification.id)) {
            return prev;
          }
          return [formattedNotification, ...prev];
        });
      },
      [formatNotification]
    ),
    // Callback for batch notifications (on connect/reconnect)
    useCallback(
      ({ notifications: newNotifications }) => {
        logger.log(`📬 Received ${newNotifications.length} notifications for dashboard`);
        const formattedNotifications = newNotifications.map(formatNotification);
        setNotifications(formattedNotifications);
      },
      [formatNotification]
    )
  );

  const fetchTodaysBookings = async () => {
    setBookingsLoading(true);
    setBookingsError(null);
    try {
      const currentUser = localService.getUser();
      const userId = currentUser?.id;

      if (!userId) {
        setBookingsError("Authentication required. Please log in.");
        return;
      }

      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const dateStr = `${yyyy}-${mm}-${dd}`;

      // Get all bookings for today in a single API call using search endpoint
      const filters = {
        userId: userId,
        startTimeFrom: convertToUTC(new Date(dateStr + "T00:00:00")),
        startTimeTo: convertToUTC(new Date(dateStr + "T23:59:59")),
        sortBy: "startTime",
        sortOrder: "asc",
        pageNumber: 0,
        pageSize: 50,
      };

      const timezone = getCurrentTimezoneOffset();
      const bookingsResponse = await spaceManagementService.searchBookings(filters, timezone);
      const bookings = bookingsResponse.data?.content || bookingsResponse.data?.data || [];

      // Sort bookings by start time
      bookings.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

      setTodaysBookings(bookings);
    } catch (error) {
      logger.error("Error fetching today's bookings:", error);
      setBookingsError("Failed to load today's bookings");
    } finally {
      setBookingsLoading(false);
    }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const currentUser = localService.getUser();
        const userId = currentUser?.id;

        if (!userId) {
          logger.error("No user ID found - user may not be logged in");
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        // Use current month for dashboard data
        const currentMonth = new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" });
        const response = await dashboardService.getDashboardData(userId, currentMonth);
        setDashboardData(response.data);

        // Don't set initial chart data from dummy service - wait for real statistics
        // Only set if booking statistics are not available (will be handled in useEffect)
        setIsLoading(false);
      } catch (error) {
        logger.error("Error fetching dashboard data:", error);
        setIsLoading(false);
      }
    };

    fetchDashboardData();
    fetchTodaysBookings();
    fetchStatistics();
    fetchBookingStatistics();
    fetchRevenueData();

    // Check if we should auto-start the tour (coming from OTP confirmation)
    const pendingTour = localStorage.getItem("pendingHostTour");
    if (pendingTour === "true") {
      // Remove the flag immediately
      localStorage.removeItem("pendingHostTour");

      // Start the tour after a short delay to ensure DOM is ready
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("startHostTour"));
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  const fetchStatistics = async () => {
    try {
      setStatisticsError(null);
      const currentUser = localService.getUser();
      const userId = currentUser?.id;

      if (!userId) {
        logger.error("No user ID found - user may not be logged in");
        setStatisticsError("User not found");
        return;
      }

      const statisticsResponse = await spaceManagementService.getSpaceStatistics(userId);

      if (statisticsResponse.status === 200) {
        setStatistics(statisticsResponse.data);
      } else {
        setStatisticsError("Failed to fetch statistics");
      }
    } catch (error) {
      logger.error("Error fetching statistics:", error);
      setStatisticsError("Failed to fetch statistics");
      // Reset to default values on error
      setStatistics({
        totalSpaces: 0,
        occupancyRate: 0,
      });
    }
  };

  const fetchBookingStatistics = async () => {
    try {
      setBookingStatisticsError(null);
      const { startDate } = getDateRange(selectedTimeRange);
      const year = new Date(startDate).getFullYear();
      const bookingStatsResponse = await spaceManagementService.getBookingStatistics(year);

      if (bookingStatsResponse.status === 200) {
        setBookingStatistics(bookingStatsResponse.data);
        logger.log("Booking statistics fetched:", bookingStatsResponse.data);
      } else {
        setBookingStatisticsError("Failed to fetch booking statistics");
        // Clear occupancy data on error
        setOccupancyData([]);
      }
    } catch (error) {
      logger.error("Error fetching booking statistics:", error);
      setBookingStatisticsError("Failed to fetch booking statistics");
      // Reset to default values on error
      setBookingStatistics({
        totalBookings: 0,
        totalActiveBookings: 0,
        percentageOccupancyRate: 0,
        activeBookingsChange: 0,
        occupancyRateChange: 0,
        activeChangeType: "NONE",
        occupancyChangeType: "NONE",
      });
      // Clear occupancy data on error
      setOccupancyData([]);
    }
  };

  const fetchRevenueData = async () => {
    try {
      setRevenueLoading(true);
      const user = localService.getUser();
      const organizationId = user?.organizationId || user?.organization?.id || user?.user?.organizationId;

      if (!organizationId) {
        logger.warn("No organization ID found for revenue data");
        setRevenueLoading(false);
        return;
      }

      const { startDate, endDate } = getDateRange(selectedTimeRange);

      const apiPeriod = selectedTimeRange.includes("Month")
        ? "MONTH"
        : selectedTimeRange.includes("Week")
          ? "WEEK"
          : "DAY";

      const response = await billingService.getOrganizationRevenueAggregates(organizationId, {
        startDate,
        endDate,
        period: apiPeriod,
      });

      if (response.data?.data) {
        // Transform API response to match chart format
        const chartData = response.data.data.map((item, index) => {
          const date = new Date(item.periodStart);
          let label;

          if (apiPeriod === "MONTH") {
            label = date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
          } else if (apiPeriod === "WEEK") {
            label = `Week ${index + 1}`;
          } else {
            // DAY
            label = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          }

          return {
            name: label,
            value: parseFloat(item.total || 0),
          };
        });

        setRevenueChartData(chartData);
      }
    } catch (error) {
      logger.error("Error fetching revenue data:", error);
      // Fallback to empty array on error
      setRevenueChartData([]);
    } finally {
      setRevenueLoading(false);
    }
  };

  // Update chart data when period changes
  useEffect(() => {
    const updateChartData = async () => {
      try {
        setOccupancyChartError(null);

        // Only use booking statistics data if available and no error
        if (bookingStatistics && !bookingStatisticsError) {
          let occupancyRatesData = null;

          switch (selectedPeriod.toLowerCase()) {
            case "daily":
              occupancyRatesData = bookingStatistics.dailyOccupancyRates;
              break;
            case "weekly":
              occupancyRatesData = bookingStatistics.weeklyOccupancyRates;
              break;
            case "monthly":
              occupancyRatesData = bookingStatistics.monthlyOccupancyRates;
              break;
            default:
              occupancyRatesData = bookingStatistics.dailyOccupancyRates;
          }

          if (occupancyRatesData && Object.keys(occupancyRatesData).length > 0) {
            // Transform the occupancy rates data to chart format with proper formatting
            const chartData = Object.entries(occupancyRatesData).map(([key, value]) => {
              let formattedName = key;

              // Format based on period type
              if (selectedPeriod.toLowerCase() === "monthly") {
                // Format month names: JANUARY -> Jan
                formattedName = key.charAt(0) + key.slice(1).toLowerCase().substring(0, 2);
              } else if (selectedPeriod.toLowerCase() === "weekly") {
                // Format week numbers: WEEK_3 -> W3
                formattedName = key.replace("WEEK_", "W");
              } else if (selectedPeriod.toLowerCase() === "daily") {
                // Format day names: WEDNESDAY -> Wed
                formattedName = key.charAt(0) + key.slice(1).toLowerCase().substring(0, 2);
              }

              return {
                name: formattedName,
                value: value,
              };
            });
            setOccupancyData(chartData);
            return;
          }
        }

        // If booking statistics failed or not available, clear the data
        // Don't use dummy data service - show empty/error state instead
        if (bookingStatisticsError) {
          setOccupancyData([]);
          setOccupancyChartError("Failed to load occupancy data");
        } else {
          // If no error but no data, just clear
          setOccupancyData([]);
        }
      } catch (error) {
        logger.error("Error fetching chart data:", error);
        setOccupancyChartError("Failed to load occupancy data");
        setOccupancyData([]);
      }
    };

    if (!isLoading) {
      updateChartData();
    }
  }, [selectedPeriod, isLoading, bookingStatistics, bookingStatisticsError]);

  // Update revenue chart data when range changes
  useEffect(() => {
    if (!isLoading) {
      fetchRevenueData();
      fetchBookingStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeRange, customStartDate, customEndDate, isLoading]);

  const _formatCurrency = value => {
    return `$${value.toLocaleString()}`;
  };

  const handleQuickAction = actionType => {
    switch (actionType) {
      case "new-booking":
        history.push("/host/calendar");
        break;
      case "add-space":
        history.push("/host/add-space");
        break;
      case "send-notifications":
        history.push("/host/announcements");
        break;
      case "add-play-service":
        history.push("/host/play-services/create");
        break;
      default:
        logger.log("Unknown action:", actionType);
    }
  };

  const handleNewSpace = () => {
    history.push("/host/add-space");
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  // Close notification modal when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications]);

  const unreadCount = notifications.filter(n => n.unread).length;

  // Render loading state
  if (isLoading) {
    return (
      <HPageContainer>
        <HHeader />
        <div className="overflow-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8 overscroll-contain">
          <div className="space-y-6 py-6">
            <div className="flex flex-col gap-4 justify-between items-start mb-6 sm:flex-row sm:items-center">
              <div className="w-48 h-6 rounded bg-slate-200"></div>
              <div className="flex gap-3 items-center w-full sm:w-auto">
                <div className="w-full h-10 rounded sm:w-48 bg-slate-200"></div>
                <div className="w-10 h-10 rounded-md bg-slate-200"></div>
                <div className="w-24 h-10 rounded-md bg-slate-200"></div>
              </div>
            </div>
            <div className="mb-6 w-64 h-6 rounded bg-slate-200"></div>
            <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4">
              <div className="h-24 rounded bg-slate-200"></div>
              <div className="h-24 rounded bg-slate-200"></div>
              <div className="h-24 rounded bg-slate-200"></div>
              <div className="h-24 rounded bg-slate-200"></div>
            </div>
            <div className="grid gap-6 mb-6 lg:grid-cols-3">
              <div className="h-96 rounded bg-slate-200 lg:col-span-2"></div>
              <div className="h-96 rounded bg-slate-200"></div>
            </div>
          </div>
        </div>
      </HPageContainer>
    );
  }

  // Always use the user's current date in their timezone
  const currentDateStr = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <HPageContainer>
      <HHeader
        rightButton={
          <div className="flex items-center gap-3">
            {selectedTimeRange === "Custom Range" && (
              <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-4 duration-300">
                <DatePicker
                  date={customStartDate}
                  setDate={setCustomStartDate}
                  placeholder="Start"
                  className="w-[130px]"
                />
                <span className="text-gray-400 text-xs">to</span>
                <DatePicker date={customEndDate} setDate={setCustomEndDate} placeholder="End" className="w-[130px]" />
              </div>
            )}
            <Select value={selectedTimeRange} onValueChange={setSelectedTimeRange}>
              <SelectTrigger className="w-[150px] bg-white border-gray-200">
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Last 7 Days">Last 7 Days</SelectItem>
                <SelectItem value="Last 30 Days">Last 30 Days</SelectItem>
                <SelectItem value="Last 90 Days">Last 90 Days</SelectItem>
                <SelectItem value="This Month">This Month</SelectItem>
                <SelectItem value="Last Month">Last Month</SelectItem>
                <SelectItem value="Custom Range">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      <div className="overflow-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8 overscroll-contain max-w-full">
        <div className="w-full max-w-full ">
          <div className="flex flex-col gap-4 justify-between items-start mb-6 sm:flex-row sm:items-center">
            <div className="flex gap-3 items-center">
              <div className="flex gap-2 items-center text-sm text-muted-foreground">
                <CalendarDays className="w-4 h-4" />
                <span className="whitespace-nowrap">{currentDateStr}</span>
              </div>
            </div>

            <div className="flex gap-3 items-center w-full sm:w-auto">
              <div className="relative">
                <button
                  className="flex justify-center items-center w-10 h-10 bg-white rounded-md border border-gray-200 hover:bg-gray-50"
                  onClick={toggleNotifications}
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="flex absolute top-0 right-0 justify-center items-center -mt-1 -mr-1 w-5 h-5 text-xs text-white bg-red-500 rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Modal */}
                {showNotifications && (
                  <div
                    className="fixed inset-x-4 top-16 z-50 max-w-sm mx-auto bg-white rounded-lg border border-gray-200 shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-12 sm:w-80"
                    ref={notificationRef}
                  >
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-gray-900">Notifications</h3>
                        {unreadCount > 0 && (
                          <button
                            className="text-sm text-blue-600 hover:text-blue-700"
                            onClick={markAllNotificationsAsRead}
                          >
                            Mark all read
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-96">
                      {notificationsLoading ? (
                        <div className="p-8 text-center text-gray-500">
                          <div className="mx-auto mb-2 w-6 h-6 rounded-full border-2 border-blue-500 animate-spin border-t-transparent"></div>
                          <p>Loading notifications...</p>
                        </div>
                      ) : notificationsError ? (
                        <div className="p-8 text-center text-red-500">
                          <Bell className="mx-auto mb-2 w-8 h-8 opacity-50" />
                          <p>{notificationsError}</p>
                        </div>
                      ) : notifications.length > 0 ? (
                        notifications.map(notification => (
                          <div
                            key={notification.id}
                            className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                              notification.unread ? "bg-blue-50" : ""
                            }`}
                            onClick={() => {
                              // Mark as read if unread
                              if (notification.unread) {
                                markNotificationAsRead(notification.id);
                              }
                            }}
                          >
                            <div className="flex items-start space-x-3">
                              <div
                                className={`w-2 h-2 rounded-full mt-2 ${
                                  notification.unread ? "bg-blue-500" : "bg-gray-300"
                                }`}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                                  {notification.priority && notification.priority !== "LOW" && (
                                    <span
                                      className={`px-2 py-1 text-xs rounded-full ${
                                        notification.priority === "HIGH"
                                          ? "bg-red-100 text-red-700"
                                          : notification.priority === "URGENT"
                                            ? "bg-orange-100 text-orange-700"
                                            : "bg-yellow-100 text-yellow-700"
                                      }`}
                                    >
                                      {notification.priority}
                                    </span>
                                  )}
                                  {notification.cta && (
                                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                                      Action Required
                                    </span>
                                  )}
                                </div>
                                <p className="mt-1 text-sm text-gray-600">{notification.message}</p>
                                <div className="flex items-center justify-between mt-2">
                                  <p className="text-xs text-gray-500">{notification.time}</p>
                                  {notification.readStatus && (
                                    <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                                      Read
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-8 text-center text-gray-500">
                          <Bell className="mx-auto mb-2 w-8 h-8 opacity-50" />
                          <p>No announcements yet</p>
                        </div>
                      )}
                    </div>
                    <div className="p-3 border-t border-gray-200">
                      <button
                        className="w-full text-sm font-medium text-blue-600 hover:text-blue-700"
                        onClick={() => history.push("/host/announcements")}
                      >
                        View all announcements
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <HActionButton
                label="New Space"
                icon={<PlusCircle className="w-4 h-4" />}
                variant="primary"
                size="default"
                onClick={handleNewSpace}
                data-tour="new-space-btn"
              />
            </div>
          </div>

          {/* Stats Section */}
          <div
            className="grid grid-cols-2 gap-2 mb-6 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4"
            data-tour="dashboard-stats"
          >
            <HStatCard
              title="Total Listings"
              value={(statistics?.totalSpaces || 0).toString()}
              details={<span className="flex gap-1 items-center text-gray-500">All your registered spaces</span>}
              detailsColor="text-gray-500"
              icon={<Building className="w-5 h-5" />}
              isLoading={isLoading}
            />
            <HStatCard
              title="Active Bookings"
              value={(bookingStatistics?.totalActiveBookings || 0).toString()}
              details={
                <span className="flex gap-1 items-center">
                  {bookingStatistics?.activeChangeType === "INCREASE" ? (
                    <ArrowUp className="w-4 h-4 text-green-500" />
                  ) : bookingStatistics?.activeChangeType === "DECREASE" ? (
                    <ArrowDown className="w-4 h-4 text-red-500" />
                  ) : null}
                  {bookingStatistics?.activeBookingsChange > 0 && bookingStatistics?.activeChangeType === "INCREASE"
                    ? "+"
                    : ""}
                  {bookingStatistics?.activeBookingsChange || 0} vs last period
                </span>
              }
              detailsColor="text-gray-500"
              icon={<Calendar className="w-5 h-5" />}
              isLoading={isLoading}
            />
            <HStatCard
              title="Total Bookings"
              value={(bookingStatistics?.totalBookings || 0).toString()}
              details={<span className="flex gap-1 items-center text-gray-500">All-time bookings</span>}
              detailsColor="text-gray-500"
              icon={<Users className="w-5 h-5" />}
              isLoading={isLoading}
            />
            <HStatCard
              title="Occupancy Rate"
              value={`${(bookingStatistics?.percentageOccupancyRate || statistics?.occupancyRate || 0).toFixed(1)}%`}
              details={
                <span className="flex gap-1 items-center">
                  {bookingStatistics?.occupancyChangeType === "INCREASE" ? (
                    <ArrowUp className="w-4 h-4 text-green-500" />
                  ) : bookingStatistics?.occupancyChangeType === "DECREASE" ? (
                    <ArrowDown className="w-4 h-4 text-red-500" />
                  ) : null}
                  {bookingStatistics?.occupancyRateChange > 0 && bookingStatistics?.occupancyChangeType === "INCREASE"
                    ? "+"
                    : ""}
                  {(bookingStatistics?.occupancyRateChange || 0).toFixed(1)}% vs last period
                </span>
              }
              detailsColor="text-gray-500"
              icon={<TrendingUp className="w-5 h-5" />}
              isLoading={isLoading}
            />
          </div>

          {/* Content Grid: Occupancy Rate + Quick Actions */}
          <div className="grid gap-6 mb-6 lg:grid-cols-3 min-w-0">
            <div className="p-3 sm:p-6 bg-white rounded-lg border border-gray-200 lg:col-span-2 overflow-hidden">
              <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:justify-between sm:items-center sm:gap-0">
                <h3 className="text-lg font-medium">Occupancy Rate</h3>
                <ViewToggle
                  options={[
                    { value: "Daily", label: "Daily" },
                    { value: "Weekly", label: "Weekly" },
                    { value: "Monthly", label: "Monthly" },
                  ]}
                  value={selectedPeriod}
                  onChange={setSelectedPeriod}
                  size="sm"
                />
              </div>
              <div className="h-[250px] sm:h-[300px] overflow-hidden min-w-0">
                {occupancyChartError || (occupancyData.length === 0 && bookingStatisticsError) ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="text-sm text-red-500 mb-2">
                        {occupancyChartError || "Failed to load occupancy data"}
                      </div>
                      <button
                        onClick={() => {
                          setOccupancyChartError(null);
                          fetchBookingStatistics();
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                ) : occupancyData.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-sm text-gray-500">No occupancy data available</div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <AreaChart data={occupancyData} margin={{ top: 5, right: 20, bottom: 5, left: 30 }}>
                      <defs>
                        <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        tick={{ fontSize: 11 }}
                        interval={0}
                      />
                      <YAxis unit="%" axisLine={false} tickLine={false} />
                      <CartesianGrid vertical={false} strokeDasharray="3 3" />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#14b8a6"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorUv)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="p-3 sm:p-6 bg-white rounded-lg border border-gray-200" data-tour="quick-actions">
              <h3 className="mb-4 text-lg font-medium">Quick Actions</h3>
              <div className="flex flex-col gap-3">
                {dashboardData.quickActions.map(action => (
                  <button
                    key={action.id}
                    className="flex gap-2 items-center px-4 py-2 text-left rounded-lg border border-gray-200 hover:bg-gray-50"
                    onClick={() => handleQuickAction(action.actionType)}
                    data-tour={action.actionType === "add-space" ? "new-space-btn-inside" : ""}
                  >
                    {action.actionType === "new-booking" && <CalendarDays className="w-4 h-4" />}
                    {action.actionType === "add-space" && <PlusCircle className="w-4 h-4" />}
                    {action.actionType === "send-notifications" && <BellRing className="w-4 h-4" />}
                    {action.actionType === "add-play-service" && <PlusCircle className="w-4 h-4" />}
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Charts Container - Today's Bookings and Revenue Overview */}
          <div className="grid gap-6 mb-6 lg:grid-cols-3 min-w-0">
            <div className="p-3 sm:p-6 bg-white rounded-lg border border-gray-200 lg:col-span-2 overflow-hidden">
              <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:justify-between sm:items-center sm:gap-0">
                <div className="flex flex-col gap-1">
                  <h3 className="text-lg font-medium">Today's Bookings</h3>
                  <p className="text-xs text-gray-500">Times shown in {formatTimezoneDisplay()}</p>
                </div>
                <a
                  href="/host/calendar"
                  className="flex gap-1 items-center px-3 py-1 text-sm font-medium text-white bg-black rounded-md"
                >
                  View All <Eye className="w-4 h-4" />
                </a>
              </div>
              <div className="overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Space
                      </th>
                      <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Client
                      </th>
                      <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Time
                      </th>
                      <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bookingsLoading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-3 text-sm text-center text-gray-500">
                          Loading today's bookings...
                        </td>
                      </tr>
                    ) : bookingsError ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-3 text-sm text-center text-red-500">
                          {bookingsError}
                        </td>
                      </tr>
                    ) : todaysBookings.length > 0 ? (
                      todaysBookings.map(booking => {
                        // Extract client info from the bookedBy object if available
                        const bookedBy = booking.bookedBy || {};
                        const firstName = bookedBy.firstName || "";
                        const lastName = bookedBy.lastName || "";
                        const username = bookedBy.username || "";
                        const imageUrl = bookedBy.imageUrl;

                        const fullName = firstName || lastName ? `${firstName} ${lastName}`.trim() : "";
                        const displayName =
                          fullName || username || booking.emails?.[0]?.split("@")[0] || "Unknown Client";
                        const displayUsername =
                          username || (booking.emails?.[0] ? booking.emails[0].split("@")[0] : "");
                        const initials =
                          firstName && lastName
                            ? `${firstName[0]}${lastName[0]}`.toUpperCase()
                            : displayName.slice(0, 2).toUpperCase();

                        return (
                          <tr key={booking.id}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                              {booking.spaceInstance?.title ||
                                booking.spaceInstance?.location?.name ||
                                getEventName(booking.spaceInstance) ||
                                `Space ${booking.spaceId}`}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                              <div className="flex gap-2 items-center">
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={displayName}
                                    className="w-7 h-7 rounded-full object-cover border border-gray-100"
                                    onError={e => {
                                      e.target.onerror = null;
                                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random`;
                                    }}
                                  />
                                ) : (
                                  <div className="flex justify-center items-center w-7 h-7 text-[10px] font-medium bg-gray-100 text-gray-600 rounded-full border border-gray-200">
                                    {initials}
                                  </div>
                                )}
                                <div className="flex flex-col min-w-0">
                                  <span className="font-medium text-gray-900 truncate">{fullName || displayName}</span>
                                  {displayUsername && (
                                    <span className="text-[10px] text-gray-500 truncate lowercase">
                                      {displayUsername.replace("@", "")}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                              {new Date(booking.startTime).toLocaleTimeString("en-US", {
                                timeZone: getUserTimezone(),
                                hour: "2-digit",
                                minute: "2-digit",
                              })}{" "}
                              -{" "}
                              {new Date(booking.endTime).toLocaleTimeString("en-US", {
                                timeZone: getUserTimezone(),
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  booking.status === "CONFIRMED"
                                    ? "bg-black text-white"
                                    : booking.status === "PENDING"
                                      ? "bg-orange-100 text-orange-600"
                                      : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {booking.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-right whitespace-nowrap">
                              <button className="text-gray-400 hover:text-gray-500">
                                <MoreHorizontal className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-3 text-sm text-center text-gray-500">
                          No bookings today
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Empty column to maintain grid alignment */}
            <div className="hidden lg:block"></div>
          </div>

          {/* Revenue Overview - Same width as Occupancy Rate */}
          <div className="grid gap-6 lg:grid-cols-3 min-w-0">
            <div className="p-3 sm:p-6 bg-white rounded-lg border border-gray-200 lg:col-span-2 overflow-hidden">
              <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:justify-between sm:items-center sm:gap-0">
                <h3 className="text-lg font-medium">Revenue Overview</h3>
                <ViewToggle
                  options={[
                    { value: "Daily", label: "Daily" },
                    { value: "Weekly", label: "Weekly" },
                    { value: "Monthly", label: "Monthly" },
                  ]}
                  value={selectedRevenuePeriod}
                  onChange={setSelectedRevenuePeriod}
                  size="sm"
                />
              </div>
              <div className="h-[250px] sm:h-[300px] overflow-hidden min-w-0">
                {revenueLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-sm text-gray-500">Loading revenue data...</div>
                  </div>
                ) : revenueChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <BarChart data={revenueChartData} margin={{ top: 5, right: 20, bottom: 5, left: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        tick={{ fontSize: 11 }}
                        interval={0}
                      />
                      <YAxis unit="k" tickFormatter={value => `$${value / 1000}`} axisLine={false} tickLine={false} />
                      <Tooltip cursor={{ fill: "transparent" }} />
                      <Bar dataKey="value" fill="#f97316" radius={[4, 4, 0, 0]} barSize={50} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-sm text-gray-500">No revenue data available</div>
                  </div>
                )}
              </div>
            </div>

            {/* Empty column to maintain grid alignment */}
            <div className="hidden lg:block"></div>
          </div>
        </div>
      </div>
    </HPageContainer>
  );
};

export default HostDashboard;
